import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Settings, Zap, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENGINE_LABELS = {
  chatgpt: "ChatGPT",
  gemini: "Gemini",
  deepseek: "DeepSeek",
};

function TypingIndicator() {
  return (
    <div data-testid="typing-indicator" className="flex items-center gap-2 px-4 py-3">
      <div className="flex items-center gap-1.5 bg-secondary rounded-2xl rounded-bl-sm px-4 py-3">
        <div className="typing-dot w-2 h-2 rounded-full bg-muted-foreground" />
        <div className="typing-dot w-2 h-2 rounded-full bg-muted-foreground" />
        <div className="typing-dot w-2 h-2 rounded-full bg-muted-foreground" />
      </div>
    </div>
  );
}

function MessageBubble({ message, index }) {
  const isUser = message.role === "user";
  return (
    <div
      data-testid={`message-${index}`}
      className={`animate-message-in flex ${isUser ? "justify-end" : "justify-start"} px-4 md:px-8`}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div
        className={`max-w-[80%] md:max-w-[65%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? "bg-primary text-primary-foreground rounded-br-sm"
            : "bg-secondary text-secondary-foreground rounded-bl-sm border border-border/50"
        }`}
      >
        <p className="whitespace-pre-wrap break-words">{message.content}</p>
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [engineInfo, setEngineInfo] = useState({ engine: "", model: "" });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchUsage();
    fetchSettings();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const fetchUsage = async () => {
    try {
      const res = await axios.get(`${API}/usage`);
      setRemaining(res.data.remaining);
    } catch {
      // ignore
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setEngineInfo({
        engine: res.data.ai_engine,
        model: res.data.ai_model,
      });
    } catch {
      // ignore
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMessage = { role: "user", content: text };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        messages: updatedMessages,
      });
      setMessages([
        ...updatedMessages,
        { role: "assistant", content: res.data.response },
      ]);
      setRemaining(res.data.remaining);
      setEngineInfo({ engine: res.data.engine, model: res.data.model });
    } catch (err) {
      const detail =
        err.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
      // Remove user message on error
      setMessages(messages);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    fetchUsage();
    toast.success("Chat cleared");
  };

  return (
    <div data-testid="chat-widget" className="flex flex-col h-screen bg-background">
      {/* Header */}
      <header className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-border/40 bg-background/80 backdrop-blur-lg z-10">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" strokeWidth={1.5} />
            <h1 className="font-heading text-lg font-semibold tracking-tight">
              Cortex
            </h1>
          </div>
          {engineInfo.engine && (
            <Badge
              data-testid="engine-badge"
              variant="secondary"
              className="text-xs font-mono"
            >
              {ENGINE_LABELS[engineInfo.engine] || engineInfo.engine} / {engineInfo.model}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {remaining !== null && (
            <span
              data-testid="remaining-count"
              className="text-xs text-muted-foreground font-mono"
            >
              {remaining} msg left
            </span>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="clear-chat-btn"
                  variant="ghost"
                  size="icon"
                  onClick={clearChat}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Clear chat</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="admin-link"
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/admin")}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                >
                  <Settings className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Admin settings</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </header>

      {/* Messages Area */}
      <ScrollArea className="flex-1">
        <div className="py-6 space-y-4 min-h-full">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4 animate-fade-in">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Zap className="h-8 w-8 text-primary" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl font-semibold mb-2 tracking-tight">
                Start a conversation
              </h2>
              <p className="text-muted-foreground text-sm max-w-md">
                Type a message below to begin chatting with the AI.
                Configure the engine and API keys in the admin panel.
              </p>
            </div>
          )}
          {messages.map((msg, i) => (
            <MessageBubble key={i} message={msg} index={i} />
          ))}
          {isLoading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="p-4 md:p-6 bg-background/80 backdrop-blur-lg border-t border-border/40">
        <div className="max-w-3xl mx-auto flex items-end gap-3">
          <div className="flex-1 relative">
            <textarea
              data-testid="chat-input"
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              rows={1}
              className="w-full resize-none rounded-xl border border-input bg-secondary/50 px-4 py-3 pr-12 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-colors font-body"
              style={{ minHeight: "48px", maxHeight: "120px" }}
              onInput={(e) => {
                e.target.style.height = "48px";
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
              }}
            />
          </div>
          <Button
            data-testid="chat-send-btn"
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="h-12 w-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 glow-primary transition-all disabled:opacity-40 disabled:shadow-none"
            size="icon"
          >
            <Send className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    </div>
  );
}
